import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Battle.css';
import AuthLayeredCard from './AuthLayeredCard';
import BattleArena from './BattleArena';
import BattleArenaBackground from './BattleArenaBackground';
// eslint-disable-next-line import/no-cycle
import { BattleCharacterPanel } from './Battle';
import { authButtonStyle, authButtonSmallStyle, authCardStyle, authStatPanelStyle } from '../constants/authStyles';
import { useCharacterOptional } from '../context/CharacterContext';
import { getWebSocketUrl } from '../utils/wsUrl';
import { playSfxFile } from '../utils/audioSettings';
import { getSkillUseSound, withSkillTypeFlags } from '../utils/battleSkillSound';
import { buildSkillEffectSheet, isPersistentSkillEffect } from '../utils/skillEffectUtils';

const replayLeadMs = 500;
const replayBeatMs = 1400;
const fightLimitMs = 120000;
const NORMAL_ATTACK_SOUND = 'sounds/normal_attack.mp3';
const ARENA_PLAYER_SHIFT = -78;
const ARENA_STANDOFF_EXTRA = 78;

function conventionCharacterBattleSprites(characterId, weaponId, armorId) {
  const cid = Number(characterId) || 1;
  const wid = Number(weaponId) || 0;
  const aid = Number(armorId) || 0;
  const reversed = aid > 0;
  const url = `image/battle/characters/${cid}-${wid}-${aid}-${reversed ? 'all-r' : 'all'}.png`;
  const allSprite = {
    url,
    frameWidth: 100,
    frameHeight: 100,
    frameCount: 5,
    sheetHeight: 300,
    fps: 8,
  };
  const fromRow = (frameRow, fps) => ({ ...allSprite, frameRow, fps });
  return {
    deathSprite: fromRow(reversed ? 2 : 0, 8),
    normalSprite: fromRow(reversed ? 0 : 1, 8),
    attackSprite: fromRow(reversed ? 1 : 2, 12),
  };
}

function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) return url;
  return `/${url}`;
}

function toMonsterSprites(sprites) {
  if (!sprites) return null;
  return {
    run: sprites.normalSprite || sprites.attackSprite,
    attack: sprites.attackSprite || sprites.normalSprite,
    death: sprites.deathSprite,
  };
}

function preloadImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url.startsWith('/') || url.startsWith('http') ? url : `/${url}`;
  });
}

function spriteUrls(sprites) {
  if (!sprites) return [];
  return [sprites.normalSprite, sprites.attackSprite, sprites.deathSprite, sprites.allSprite]
    .map((sheet) => sheet?.url)
    .filter(Boolean);
}

async function loadFighterLook(name) {
  const empty = {
    sprites: conventionCharacterBattleSprites(1, 0, 0),
    items: [],
    character: null,
  };
  if (!name) return empty;
  try {
    const charRes = await axios.get(`/api/user-character?username=${encodeURIComponent(name)}`);
    const character = charRes.data?.character;
    if (!character) return empty;
    const eqRes = await axios.get(`/api/equipped-items?username=${encodeURIComponent(name)}`);
    const items = eqRes.data?.items || [];
    const weapon = items.find((item) => item.slotType === 'weapon');
    const armor = items.find((item) => item.slotType === 'armor');
    const cid = character.characterId || 1;
    const wid = weapon?.baseItemId ?? 0;
    const aid = armor?.baseItemId ?? 0;
    let sprites = conventionCharacterBattleSprites(cid, wid, aid);
    try {
      const spriteRes = await axios.get(
        `/api/battle/character-sprites?characterId=${cid}&weaponId=${wid}&armorId=${aid}`
      );
      if (spriteRes.data?.success && spriteRes.data.sprites) sprites = spriteRes.data.sprites;
    } catch {
      /* convention sheet */
    }
    return { sprites, items, character };
  } catch {
    return empty;
  }
}

export default function ArenaBattleScreen({ offlineFight, onlineMatch }) {
  const { t } = useTranslation('battle');
  const { t: ta } = useTranslation('arena');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const characterCtx = useCharacterOptional();
  const userCharacter = characterCtx?.character;
  const username = localStorage.getItem('username') || '';
  const online = Boolean(onlineMatch?.matchId);

  const attMax = offlineFight?.attacker?.maxHp || 0;
  const defMax = offlineFight?.defender?.maxHp || 0;
  const attMpMax = offlineFight?.attacker?.maxMp || 0;
  const defMpMax = offlineFight?.defender?.maxMp || 0;

  const [youHp, setYouHp] = useState(attMax);
  const [youMp, setYouMp] = useState(attMpMax);
  const [foeHp, setFoeHp] = useState(defMax);
  const [foeMp, setFoeMp] = useState(defMpMax);
  const [youMaxHp, setYouMaxHp] = useState(attMax);
  const [youMaxMp, setYouMaxMp] = useState(attMpMax);
  const [foeMaxHp, setFoeMaxHp] = useState(defMax);
  const [foeMaxMp, setFoeMaxMp] = useState(defMpMax);
  const [youName, setYouName] = useState(offlineFight?.attacker?.username || username);
  const [foeName, setFoeName] = useState(offlineFight?.defender?.username || '');
  const [screenTab, setScreenTab] = useState('battle');
  const [logLines, setLogLines] = useState([]);
  const [skillVisualEffects, setSkillVisualEffects] = useState({});
  const [foeSkillEffect, setFoeSkillEffect] = useState(null);
  const [youAnim, setYouAnim] = useState('normal');
  const [foeAnim, setFoeAnim] = useState('idle');
  const [youFloat, setYouFloat] = useState(null);
  const [foeFloat, setFoeFloat] = useState(null);
  const [doneText, setDoneText] = useState('');
  const [resultPopup, setResultPopup] = useState(null);
  const [remainSec, setRemainSec] = useState(null);
  const [gate, setGate] = useState('fighting');
  const [countUntil, setCountUntil] = useState(0);
  const [countLeft, setCountLeft] = useState(0);
  const [limitSec, setLimitSec] = useState(fightLimitMs / 1000);
  const [youSprites, setYouSprites] = useState(null);
  const [foeSprites, setFoeSprites] = useState(null);
  const [lookReady, setLookReady] = useState(false);
  const [lookError, setLookError] = useState('');
  const [battleBg, setBattleBg] = useState('');
  const [skills, setSkills] = useState([]);
  const [onlineSkills, setOnlineSkills] = useState([]);
  const [normalAttackCooldownUntil, setNormalAttackCooldownUntil] = useState(0);
  const [normalAttackRemainingSec, setNormalAttackRemainingSec] = useState(0);
  const [skillCooldowns, setSkillCooldowns] = useState({});
  const [skillCooldownTick, setSkillCooldownTick] = useState(0);
  const wsRef = useRef(null);
  const fightEndedRef = useRef(false);
  const clockArmedRef = useRef(false);
  const endsAtRef = useRef(0);
  const [socketTry, setSocketTry] = useState(0);
  const youIdRef = useRef(offlineFight?.attacker?.userId || 0);
  const youToken = useRef(0);
  const foeToken = useRef(0);
  const hitSeq = useRef(0);
  // Can/mana yenilenmesi: canavar savaşındaki gibi çubukların üstünde +N
  const [youHpFloats, setYouHpFloats] = useState([]);
  const [youMpFloats, setYouMpFloats] = useState([]);
  const [foeHpFloats, setFoeHpFloats] = useState([]);
  const [foeMpFloats, setFoeMpFloats] = useState([]);
  const regenSeq = useRef(0);
  const showRegen = (isYou, hpGain, mpGain) => {
    const push = (setter, value) => {
      if (!value || value <= 0) return;
      regenSeq.current += 1;
      const id = `regen-${Date.now()}-${regenSeq.current}`;
      setter((prev) => [...prev.slice(-3), { id, value, heal: true }]);
      window.setTimeout(() => setter((prev) => prev.filter((f) => f.id !== id)), 1100);
    };
    push(isYou ? setYouHpFloats : setFoeHpFloats, hpGain);
    push(isYou ? setYouMpFloats : setFoeMpFloats, mpGain);
  };
  const skillsRef = useRef([]);
  const logRef = useRef(null);
  const youNameRef = useRef(youName);
  const foeNameRef = useRef(foeName);
  const tRef = useRef(t);
  const taRef = useRef(ta);
  youNameRef.current = youName;
  foeNameRef.current = foeName;
  tRef.current = t;
  taRef.current = ta;

  const armClock = useCallback((elapsedMs, timeoutMs) => {
    const limit = timeoutMs > 0 ? timeoutMs : fightLimitMs;
    const left = Math.max(0, limit - Math.max(0, elapsedMs || 0));
    endsAtRef.current = Date.now() + left;
    setLimitSec(Math.max(1, Math.round(limit / 1000)));
    setRemainSec(Math.ceil(left / 1000));
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (endsAtRef.current <= 0 || fightEndedRef.current) return;
      const left = Math.max(0, Math.ceil((endsAtRef.current - Date.now()) / 1000));
      setRemainSec(left);
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!countUntil) {
      setCountLeft(0);
      return undefined;
    }
    const tick = () => setCountLeft(Math.max(0, Math.ceil((countUntil - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [countUntil]);

  useEffect(() => {
    if (online || !offlineFight || !lookReady || clockArmedRef.current) return;
    clockArmedRef.current = true;
    armClock(0, fightLimitMs);
  }, [online, offlineFight, lookReady, armClock]);

  useEffect(() => {
    if (normalAttackCooldownUntil <= Date.now()) {
      setNormalAttackRemainingSec(0);
      return undefined;
    }
    const update = () => {
      const rem = Math.ceil((normalAttackCooldownUntil - Date.now()) / 1000);
      setNormalAttackRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = window.setInterval(update, 100);
    return () => window.clearInterval(id);
  }, [normalAttackCooldownUntil]);

  useEffect(() => {
    const hasAny = Object.values(skillCooldowns).some((until) => until > Date.now());
    if (!hasAny) return undefined;
    const id = window.setInterval(() => setSkillCooldownTick((n) => n + 1), 100);
    return () => window.clearInterval(id);
  }, [skillCooldowns]);

  useEffect(() => {
    if (!offlineFight && !online) {
      navigate('/arena', { replace: true });
    }
  }, [offlineFight, online, navigate]);

  useEffect(() => {
    let cancelled = false;
    const youUser = offlineFight?.attacker?.username || username;
    const foeUser = offlineFight?.defender?.username || '';
    const loadLook = async () => {
      setLookReady(false);
      setLookError('');
      try {
      const you = await loadFighterLook(youUser);
      if (cancelled) return;
      setYouSprites(you.sprites);
      if (you.character?.characterName) setYouName(you.character.characterName);

      let foeSpritesNext = null;
      if (foeUser) {
        const foe = await loadFighterLook(foeUser);
        if (cancelled) return;
        foeSpritesNext = foe.sprites;
        setFoeSprites(foe.sprites);
      }

      try {
        const villagesRes = await axios.get('/api/villages');
        const village = (villagesRes.data?.villages || []).find(
          (row) => Number(row.id) === Number(you.character?.villageId)
        );
        if (!cancelled && village?.battleBackgroundUrl) {
          setBattleBg(normalizeImageUrl(village.battleBackgroundUrl));
        }
      } catch {
        /* background optional */
      }

      try {
        const skillsRes = await axios.get(`/api/battle/skills?username=${encodeURIComponent(username)}`);
        if (!cancelled && skillsRes.data?.success) {
          const nextSkills = skillsRes.data.skills || [];
          skillsRef.current = nextSkills;
          setSkills(nextSkills);
        }
      } catch {
        /* skills optional for the replay */
      }

      const urls = [...spriteUrls(you.sprites), ...spriteUrls(foeSpritesNext)];
      (skillsRef.current || []).forEach((sk) => {
        if (sk.effectFile) urls.push(sk.effectFile);
      });
      (offlineFight?.events || []).forEach((ev) => {
        if (ev.type === 'skill' && ev.skillId) urls.push(`image/skills/effects/${ev.skillId}.png`);
      });
      await Promise.all(urls.map(preloadImage));
      if (!cancelled) setLookReady(true);
      } catch (e) {
        if (!cancelled) setLookError(e?.message || 'error');
      }
    };
    loadLook();
    return () => {
      cancelled = true;
    };
  }, [username, offlineFight]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines, doneText]);

  const pushLog = (who, detail, kind = 'cast') => {
    setLogLines((prev) => [...prev, { id: `${prev.length}-${kind}`, who, detail, kind }]);
  };

  const playCast = (skill, actorIsYou, token) => {
    if (!skill) {
      playSfxFile(NORMAL_ATTACK_SOUND, 0.5, 'character');
      return;
    }
    const sound = getSkillUseSound(skill);
    if (sound) playSfxFile(sound, 1, 'character');
    const sheet = buildSkillEffectSheet(skill);
    if (!sheet) return;
    const persistent = isPersistentSkillEffect(skill);
    const effect = {
      sheet,
      loop: persistent,
      untilMs: 0,
      key: token,
    };
    // Buff skilleri kullananın üstünde, saldırı skilleri rakibin üstünde görünür.
    const showOnYou = persistent ? actorIsYou : !actorIsYou;
    if (showOnYou) {
      setSkillVisualEffects((prev) => ({ ...prev, [skill.id || token]: effect }));
    } else {
      setFoeSkillEffect(effect);
    }
  };

  const resolveSkill = (skillId, skillName) => {
    const list = skillsRef.current || [];
    const found = list.find((row) => (skillId && row.id === skillId) || (skillName && row.skillName === skillName));
    if (found) return withSkillTypeFlags(found);
    if (!skillId && !skillName) return null;
    return withSkillTypeFlags({ id: skillId, skillName: skillName || '' });
  };

  const pulse = (actorIsYou) => {
    if (actorIsYou) {
      const token = youToken.current + 1;
      youToken.current = token;
      setYouAnim('attack');
      window.setTimeout(() => {
        if (youToken.current === token) setYouAnim('normal');
      }, 460);
    } else {
      const token = foeToken.current + 1;
      foeToken.current = token;
      setFoeAnim('attack');
      window.setTimeout(() => {
        if (foeToken.current === token) setFoeAnim('idle');
      }, 460);
    }
  };

  const showHit = (actorIsYou, damage, critical, pierced, id) => {
    if (!damage) return;
    const float = { id: `${id}-${hitSeq.current}`, value: damage, critical, pierced, heal: false };
    hitSeq.current += 1;
    if (actorIsYou) {
      setFoeFloat(float);
      window.setTimeout(() => {
        setFoeFloat((prev) => (prev && prev.id === float.id ? null : prev));
      }, 1500);
    } else {
      setYouFloat(float);
      window.setTimeout(() => {
        setYouFloat((prev) => (prev && prev.id === float.id ? null : prev));
      }, 1500);
    }
  };

  useEffect(() => {
    if (!online || offlineFight?.defender?.username || !foeName) return undefined;
    let cancelled = false;
    loadFighterLook(foeName).then(async (foe) => {
      if (cancelled || !foe.sprites) return;
      await Promise.all(spriteUrls(foe.sprites).map(preloadImage));
      if (!cancelled) setFoeSprites(foe.sprites);
    });
    return () => {
      cancelled = true;
    };
  }, [online, foeName, offlineFight?.defender?.username]);

  useEffect(() => {
    if (!offlineFight || !lookReady) return undefined;
    setYouHp(attMax);
    setYouMp(attMpMax);
    setFoeHp(defMax);
    setFoeMp(defMpMax);
    setYouMaxHp(attMax);
    setYouMaxMp(attMpMax);
    setFoeMaxHp(defMax);
    setFoeMaxMp(defMpMax);
    setDoneText('');
    setResultPopup(null);
    setLogLines([]);
    setYouAnim('normal');
    setFoeAnim('idle');
    fightEndedRef.current = false;

    const events = offlineFight.events || [];
    const showResult = () => {
      const draw = offlineFight.reason === 'draw';
      const won = !draw && offlineFight.winnerUserId === offlineFight.attacker?.userId;
      fightEndedRef.current = true;
      setDoneText(draw ? ta('draw') : won ? ta('victory') : ta('defeat'));
      setResultPopup({
        draw,
        won,
        delta: draw ? 0 : (offlineFight.attackerPointsDelta || 0),
        kind: offlineFight.replayKind === 'friend' ? 'friend' : offlineFight.replayKind === 'online' ? 'rating' : 'points',
      });
    };
    if (!events.length) {
      showResult();
      return undefined;
    }

    const beats = [];
    events.forEach((ev) => {
      const last = beats[beats.length - 1];
      if (ev.type === 'regen' && last) {
        last.push(ev);
        return;
      }
      const continues = last
        && last[0].t === ev.t
        && last[0].actorUserId === ev.actorUserId
        && ev.type !== 'attack'
        && ev.type !== 'skill';
      if (continues) last.push(ev);
      else beats.push([ev]);
    });

    let i = 0;
    let hpA = attMax;
    let hpD = defMax;
    let timer = null;
    const finish = () => {
      showResult();
    };
    const step = () => {
      if (i >= beats.length) {
        finish();
        return;
      }
      const beat = beats[i];
      i += 1;
      beat.forEach((ev, beatIndex) => {
        const prevA = hpA;
        const prevD = hpD;
        hpA = ev.attackerHp ?? hpA;
        hpD = ev.defenderHp ?? hpD;
        setYouHp(hpA);
        setFoeHp(hpD);
        setYouMp(ev.attackerMp ?? attMpMax);
        setFoeMp(ev.defenderMp ?? defMpMax);

        const actorIsYou = ev.actorUserId === offlineFight.attacker?.userId;
        const actorName = actorIsYou ? offlineFight.attacker?.username : offlineFight.defender?.username;
        const targetName = actorIsYou ? offlineFight.defender?.username : offlineFight.attacker?.username;
        if (ev.type === 'attack' || ev.type === 'skill') {
          pulse(actorIsYou);
          const skill = ev.type === 'skill' ? resolveSkill(ev.skillId, ev.skillName) : null;
          const label = ev.type === 'skill' ? (ev.skillName || skill?.skillName || t('attackButton')) : t('attackButton');
          playCast(ev.type === 'skill' ? skill : null, actorIsYou, `${i}-${beatIndex}-${ev.t}`);
          pushLog(actorName, label);
        }
        if (ev.type === 'hit' && ev.damage > 0) {
          showHit(actorIsYou, ev.damage, ev.critical, ev.pierced, `${i}-${beatIndex}-${ev.t}`);
          pushLog(targetName, `-${ev.damage}`, 'hit');
        }
        if (ev.type === 'regen') showRegen(actorIsYou, ev.hpGain, ev.mpGain);
        if (hpA <= 0) {
          youToken.current += 1;
          setYouAnim('normal');
        }
        if (prevA > 0 && hpA <= 0) setYouAnim('normal');
        if (hpD <= 0) {
          foeToken.current += 1;
          setFoeAnim('death');
        }
        if (prevD > hpD && hpD <= 0) setFoeAnim('death');
      });
      timer = window.setTimeout(step, replayBeatMs);
    };
    const kickoff = window.setTimeout(step, replayLeadMs);

    return () => {
      window.clearTimeout(kickoff);
      if (timer) window.clearTimeout(timer);
    };
  }, [offlineFight, lookReady, attMax, defMax, attMpMax, defMpMax, ta, t]);

  useEffect(() => {
    if (!online) return undefined;
    fightEndedRef.current = false;
    clockArmedRef.current = false;
    setGate('fighting');
    setCountUntil(0);
  }, [online, onlineMatch?.matchId]);

  useEffect(() => {
    if (!online) return undefined;
    let alive = true;
    let retryTimer = 0;
    const url = getWebSocketUrl(`/ws/arena?token=${encodeURIComponent(localStorage.getItem('token') || '')}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'join', matchId: onlineMatch.matchId }));
    };
    ws.onclose = () => {
      if (!alive || fightEndedRef.current) return;
      retryTimer = window.setTimeout(() => {
        if (alive && !fightEndedRef.current) setSocketTry((n) => n + 1);
      }, 800);
    };
    ws.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.type === 'state') {
        youIdRef.current = msg.you?.userId || youIdRef.current;
        setYouName(msg.you?.username || username);
        setFoeName(msg.opponent?.username || '');
        setYouHp(msg.yourHp ?? 0);
        setYouMp(msg.yourMp ?? 0);
        setFoeHp(msg.opponentHp ?? 0);
        setFoeMp(msg.opponentMp ?? 0);
        setYouMaxHp(msg.yourMaxHp || msg.yourHp || 0);
        setYouMaxMp(msg.yourMaxMp || msg.yourMp || 0);
        setFoeMaxHp(msg.opponentMaxHp || msg.opponentHp || 0);
        setFoeMaxMp(msg.opponentMaxMp || msg.opponentMp || 0);
        setOnlineSkills(msg.skills || []);
        const normalMs = Number(msg.normalAttackCooldownMs) || 0;
        setNormalAttackCooldownUntil(normalMs > 0 ? Date.now() + normalMs : 0);
        const nextCooldowns = {};
        (msg.skills || []).forEach((sk) => {
          const ms = Number(sk.cooldownMs) || 0;
          if (ms > 0 && sk.skillId) nextCooldowns[sk.skillId] = Date.now() + ms;
        });
        setSkillCooldowns(nextCooldowns);
        const phase = msg.phase || 'fighting';
        setGate(phase);
        if (phase === 'countdown') {
          setCountUntil(Date.now() + (Number(msg.countdownMs) || 0));
        } else {
          setCountUntil(0);
        }
        if (phase === 'fighting' && !clockArmedRef.current) {
          clockArmedRef.current = true;
          armClock(Number(msg.elapsedMs) || 0, Number(msg.timeoutMs) || fightLimitMs);
        }
      }
      if (msg.type === 'event') {
        const actorIsYou = msg.actorUserId === youIdRef.current;
        setYouHp(msg.yourHp ?? 0);
        setYouMp(msg.yourMp ?? 0);
        setFoeHp(msg.opponentHp ?? 0);
        setFoeMp(msg.opponentMp ?? 0);
        if (msg.action === 'attack' || msg.action === 'skill') {
          pulse(actorIsYou);
          const skill = msg.action === 'skill' ? resolveSkill(msg.skillId, msg.skillName) : null;
          const label = msg.action === 'skill' ? (msg.skillName || tRef.current('attackButton')) : tRef.current('attackButton');
          playCast(msg.action === 'skill' ? skill : null, actorIsYou, `${msg.t}-${msg.action}`);
          const who = actorIsYou ? youNameRef.current : foeNameRef.current;
          pushLog(who, label);
        }
        if (msg.action === 'regen') showRegen(actorIsYou, msg.hpGain, msg.mpGain);
        if (msg.action === 'hit' && msg.damage > 0) {
          showHit(actorIsYou, msg.damage, msg.critical, msg.pierced, `${msg.t}-${msg.damage}`);
          const target = actorIsYou ? foeNameRef.current : youNameRef.current;
          pushLog(target, `-${msg.damage}`, 'hit');
        }
        const foeDead = (msg.opponentHp ?? 1) <= 0;
        const youDeadNow = (msg.yourHp ?? 1) <= 0;
        if (foeDead) {
          foeToken.current += 1;
          setFoeAnim('death');
        }
        if (youDeadNow) {
          youToken.current += 1;
          setYouAnim('normal');
        }
      }
      if (msg.type === 'end' || msg.ended) {
        fightEndedRef.current = true;
      }
      if (msg.type === 'end') {
        const draw = msg.reason === 'draw';
        const won = !draw && msg.winnerUserId === youIdRef.current;
        const delta = draw ? 0 : msg.yourRatingDelta;
        setDoneText(draw ? taRef.current('draw') : won ? taRef.current('victory') : taRef.current('defeat'));
        setResultPopup({ draw, won, delta: msg.friendly ? 0 : (delta || 0), kind: msg.friendly ? 'friend' : 'rating' });
        if (!draw && won) {
          foeToken.current += 1;
          setFoeAnim('death');
        } else if (!draw) {
          youToken.current += 1;
          setYouAnim('normal');
        }
      }
      if (msg.type === 'error' && msg.message) pushLog('', msg.message, 'cast');
    };
    return () => {
      alive = false;
      window.clearTimeout(retryTimer);
      ws.close();
    };
  }, [online, onlineMatch?.matchId, username, socketTry, armClock]);

  const send = (payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  };

  const gated = online && gate !== 'fighting';
  const youDead = youHp <= 0 && youMaxHp > 0;
  const foe = foeName ? {
    id: 'arena-foe',
    name: foeName,
    hp: Math.max(0, foeHp),
    maxHp: foeMaxHp || foeHp || 1,
    mp: Math.max(0, foeMp),
    maxMp: foeMaxMp || 0,
    side: 'right',
    originSide: 'right',
    slotIndex: 0,
    spawnPhase: 'ready',
    anim: foeHp <= 0 && foeMaxHp > 0 ? 'death' : foeAnim,
    battleSprites: toMonsterSprites(foeSprites),
    standoffExtra: ARENA_STANDOFF_EXTRA,
    skillEffect: foeSkillEffect,
    hpFloats: foeHpFloats,
    mpFloats: foeMpFloats,
  } : null;

  const shownSkills = online
    ? onlineSkills.map((sk) => {
      const match = skills.find((row) => row.id === sk.skillId || row.skillName === sk.name);
      return {
        id: sk.skillId,
        skillName: sk.name,
        imageUrl: match?.imageUrl,
        requiredMana: sk.requiredMana || 0,
      };
    })
    : skills;

  const level = userCharacter?.level || offlineFight?.attacker?.level || 1;
  const expCurrent = userCharacter?.currentLevelExp || 0;
  const expNext = userCharacter?.expToNextLevel || 0;
  const expPct = expNext > 0 ? (expCurrent / (expCurrent + expNext)) * 100 : 100;

  return (
    <div className="auth-container auth-container--selection battle-page">
      <AuthLayeredCard>
        <div className="battle-nav-row">
          <button
            type="button"
            className="auth-button auth-button--compact battle-nav-btn battle-nav-btn--back"
            style={authButtonSmallStyle}
            onClick={() => {
              if (offlineFight && !lookReady && !lookError) return;
              navigate('/arena');
            }}
            disabled={Boolean(offlineFight) && !lookReady && !lookError}
            aria-label={tc('back')}
          >
            ←
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${screenTab === 'battle' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => setScreenTab('battle')}
          >
            {t('navBattle')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${screenTab === 'character' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => setScreenTab('character')}
          >
            {t('navCharacter')}
          </button>
        </div>
        {remainSec != null && !doneText ? (
          <div className="arena-timebar">
            <span className="arena-timebar-count">{remainSec}</span>
            <span className="arena-timebar-note">{ta('drawAfter', { sec: limitSec })}</span>
          </div>
        ) : null}
        <p className="battle-village-line">
          {youName} vs {foeName || '…'}
        </p>
        <div className="auth-selection-body battle-body">
          {screenTab === 'character' && <BattleCharacterPanel />}

          {screenTab === 'battle' && (
          <>
          <div className="monsters-section monsters-section--arena battle-stat-panel" style={authStatPanelStyle}>
            <div className="monsters-header">
              <div className="monsters-title-row">
                <div className="level-bar-compact">
                  <span className="level-text-compact">Lv.{level}</span>
                  <div className="level-bar-wrap">
                    <div className="level-bar-container">
                      <div className="level-bar-fill" style={{ width: `${expPct}%` }} />
                      <div className="level-bar-text">
                        {expNext > 0 ? `${expCurrent} / ${expCurrent + expNext} EXP` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="monsters-arena-wrap">
              {battleBg ? <BattleArenaBackground imageUrl={battleBg} /> : null}
              <BattleArena
                monsters={foe ? [foe] : []}
                monsterDamageFloats={foeFloat ? { 'arena-foe': foeFloat } : {}}
                characterBattleSprites={youSprites}
                characterAnim={youDead ? 'normal' : youAnim}
                characterHP={youMaxHp > 0 ? youHp : 1}
                characterMaxHP={youMaxHp > 0 ? youMaxHp : 1}
                characterMP={youMaxMp > 0 ? youMp : 1}
                characterMaxMP={youMaxMp > 0 ? youMaxMp : 1}
                characterDamageFloat={youFloat}
                characterHpFloats={youHpFloats}
                characterMpFloats={youMpFloats}
                characterShiftX={ARENA_PLAYER_SHIFT}
                characterLabel={youName}
                characterDeathActive={youDead}
                skillVisualEffects={skillVisualEffects}
                onSkillEffectComplete={(skillId) => {
                  setSkillVisualEffects((prev) => {
                    if (!prev[skillId]) return prev;
                    const next = { ...prev };
                    delete next[skillId];
                    return next;
                  });
                }}
                onMonsterSkillEffectComplete={() => setFoeSkillEffect(null)}
                emptyLabel=""
                hideMonstersWhenDead={false}
              />
              {gate === 'waiting' ? (
                <div className="arena-countdown arena-countdown--wait">{ta('waitingOpponent')}</div>
              ) : null}
              {gate === 'countdown' ? (
                <div className="arena-countdown" aria-live="assertive">{Math.max(1, countLeft)}</div>
              ) : null}
              <span className="monster-count">{foe ? 1 : 0}</span>
            </div>
          </div>

          {online && (
            <div className="battle-actions battle-actions--arena-skills">
              <div className="action-buttons-1">
                {shownSkills.map((skill) => {
                  const displayName = skill.skillName || t('skillFallback', { id: skill.id });
                  const until = skillCooldowns[skill.id] || 0;
                  const nowMs = skillCooldownTick >= 0 ? Date.now() : Date.now();
                  const isOnCooldown = until > nowMs;
                  const skillRemainingSec = isOnCooldown
                    ? Math.max(0, Math.ceil((until - nowMs) / 1000))
                    : 0;
                  const canUse = !gated && !doneText && !isOnCooldown && youMp >= (skill.requiredMana || 0);
                  return (
                    <button
                      key={skill.id}
                      type="button"
                      className={`skill-button ${canUse ? '' : 'disabled'}`}
                      disabled={!canUse}
                      aria-label={displayName}
                      title={isOnCooldown ? t('skillsCooldown', { sec: skillRemainingSec }) : displayName}
                      onClick={() => canUse && send({ type: 'skill', skillId: skill.id })}
                    >
                      {skill.imageUrl ? (
                        <img className="skill-button-image" src={normalizeImageUrl(skill.imageUrl)} alt={displayName} />
                      ) : (
                        <span className="skill-button-fallback">{displayName}</span>
                      )}
                      {isOnCooldown && skillRemainingSec > 0 ? (
                        <span className="skill-button-cooldown">{skillRemainingSec}s</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {online && (
          <div className="battle-actions">
            <div className="action-buttons">
              <button
                type="button"
                className={`auth-button attack-button ${gated || doneText || normalAttackRemainingSec > 0 ? 'disabled' : ''}`}
                style={{ ...authButtonStyle, gridColumn: '1 / -1' }}
                disabled={gated || Boolean(doneText) || normalAttackRemainingSec > 0}
                onClick={() => send({ type: 'attack' })}
              >
                {normalAttackRemainingSec > 0 ? t('attackCooldown', { sec: normalAttackRemainingSec }) : t('attackButton')}
              </button>
            </div>
          </div>
          )}

          <div ref={logRef} className="arena-hit-log battle-stat-panel" style={authStatPanelStyle}>
            {logLines.length === 0 ? (
              <div className="arena-hit-log-line">
                {online && !foeName ? ta('connecting') : t('dropsNone')}
              </div>
            ) : logLines.map((row) => (
              <div key={row.id} className={`arena-hit-log-line${row.kind === 'hit' ? ' arena-hit-log-line--hit' : ''}`}>
                {row.who ? <span className="arena-hit-log-who">{row.who}</span> : null}
                <span className="arena-hit-log-what">{row.detail}</span>
              </div>
            ))}
            {doneText ? <div className="arena-hit-log-line"><strong>{doneText}</strong></div> : null}
          </div>

          <div className="character-stats-compact battle-stat-panel" style={authStatPanelStyle}>
            <div className="buff-indicators-area">
              <button type="button" className="battle-bonuses-btn battle-bonuses-btn--empty" disabled>
                {t('buffNone')}
              </button>
            </div>
          </div>
          </>
          )}
        </div>
      </AuthLayeredCard>
      {offlineFight && !lookReady && (
        <div className="popup-overlay" role="alertdialog" aria-busy={!lookError} aria-label={ta('fightLoading')}>
          <div className="popup-content popup-content-small auth-card" style={authCardStyle}>
            {lookError ? (
              <>
                <p className="arena-result-line">{ta('errorFight')}</p>
                <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="auth-button popup-button"
                    style={authButtonStyle}
                    onClick={() => navigate('/arena')}
                  >
                    {tc('ok')}
                  </button>
                </div>
              </>
            ) : (
              <p className="arena-result-line">{ta('fightLoading')}</p>
            )}
          </div>
        </div>
      )}
      {resultPopup && (
        <div className="popup-overlay">
          <div className="popup-content popup-content-small auth-card" style={authCardStyle}>
            <div className="popup-header">
              <h3>{resultPopup.draw ? ta('draw') : resultPopup.won ? ta('victory') : ta('defeat')}</h3>
            </div>
            <div className="popup-body">
              <p className="arena-result-line">
                {resultPopup.kind === 'friend' ? ta('friendNoPoints') : resultPopup.delta > 0
                  ? ta('pointsUp', {
                    label: resultPopup.kind === 'rating' ? ta('rating') : ta('points'),
                    value: resultPopup.delta,
                  })
                  : resultPopup.delta < 0
                    ? ta('pointsDown', {
                      label: resultPopup.kind === 'rating' ? ta('rating') : ta('points'),
                      value: Math.abs(resultPopup.delta),
                    })
                    : ta('pointsSame', {
                      label: resultPopup.kind === 'rating' ? ta('rating') : ta('points'),
                    })}
              </p>
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => (resultPopup.kind === 'friend'
                  ? navigate('/profile?tab=history')
                  : navigate('/arena', {
                    state: { arenaTab: resultPopup.kind === 'rating' ? 'online' : 'offline' },
                  }))}
              >
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
